import { useState } from "react";
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip, Box, CircularProgress, Typography, } from "@mui/material";
import { useSnackbar } from "notistack";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import moment from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { checkApiStatus, numberWithCommas } from "../../../../utils";
import { addAmendmentsApi, updateAmendmentsApi } from "../apiService";
import { globalPostService, globalPutService } from "../../../../utils/globalApiServices";
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { AttachementsUpload, ButtonLoader } from "../../../../shared_elements/components";
import { regexConstants } from "../../../../constants/regEx";
import { useParams } from "react-router";
import { backendDateFormat, fieldDateFormat } from "../../../../constants";
import { setFileUploadDetails } from "../../../../shared_elements/actions";
import { useDispatch, useSelector } from "react-redux";
import { trackActivity } from "../../../../utils/mixpanel";
import { formatBytes } from "../../../../utils/fileUpload";
import LessMoreTextFieldComp from "../../../../shared_elements/components/LessMoreTextFieldComp";

const AmendmentCRU = ({ addEditAmendment,fileUpload, modeType, getResponseBack, onToggleModal, onOpenAttachments }) => {
  const params = useParams()
  const dispatch=useDispatch()
  const { storageDetails } = useSelector((state) => state.leases);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState(addEditAmendment?.data);
  const [error, setError] = useState({});
  const [attachments, setAttachments] = useState([]);

  const onRestErrorKey = (key) => {
    setError((prev) => ({ ...prev, [key]: '' }));
  };

  const onFieldChange = (key, value) => {
    setData({ ...data, [key]: value });
    setError((prev) => ({ ...prev, [key]: '' }));
  };

  const handleFileChange = (files) => {
    setAttachments(files);
    dispatch(setFileUploadDetails({attachments:files}))
    
  };

  const onAddEditAmendment = () => {
    const validationInputs = {
      date: data?.date ? "" : "Please select Amendment Date",
      unique_identifier: data?.unique_identifier?.trim()?.length ? "" : "Please enter Amendment Number",
      description: data?.description?.trim()?.length ? "" : "Please enter Amendment Description",
    };

    const amendmentDate = moment(data?.date);
    if (data?.date && !amendmentDate.isValid()) {
      validationInputs.date = "Please enter valid Amendment Date";
    }

    if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
      setLoading(true);
      let payload = Object.assign({}, data);
      payload = {
        ...payload,
        tsn: payload.tsn ? payload.tsn : null,
        csn: payload.csn ? payload.csn : null
      }
      if (data.id) {
        globalPutService(updateAmendmentsApi({ ...params, slug: data?.slug }), payload).then(async (response) => {
          if (checkApiStatus(response)) {
            if (attachments?.length) {
              fileUpload(data?.slug,'updated')
            }
            enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
            onToggleModal()
            getResponseBack();
            setLoading(false);
            trackActivity('Item Edited', { page_title: `Edit Amendment`, success_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
          } else {
            setLoading(false);
            enqueueSnackbar(response.data.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
            trackActivity('Item Edited', { page_title: `Edit Amendment`, error_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
          }
        });
      } else {
        const { lessee, lessor, ...cleanedData } = data
        globalPostService(addAmendmentsApi((params), { ...cleanedData, lessee_id: data?.lessee, lessor_id: data?.lessor }), payload)
        .then(async (response) => {
          if (checkApiStatus(response)) {
            if (attachments?.length) {
              fileUpload(response?.data?.data?.slug,'created')
            }
            enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" }, });
            onToggleModal()
            getResponseBack();
            setLoading(false);
            trackActivity('Item Added', { page_title: `Add Amendment`, success_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: data, response: response?.data })
          } else {
            setLoading(false);
            enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
            trackActivity('Item Added', { page_title: `Add Amendment`, error_msg: response?.data?.message, item_id: null,payload:data, response:response?.data })
          }
        });
      }
    } else {
      setError(validationInputs);
      trackActivity(`Item ${modeType==='add'?'Added':'Edited'}`, { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Amendment`, error_msg:validationInputs, })
    }
  };
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Amendment`, page_type: page_type, })
  }

  return (
    <>
      <Dialog
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            onToggleModal();
          }
        }}
        disableEscapeKeyDown
        aria-labelledby="customized-dialog-title"
        className="add-lease-dialog"
        maxWidth="md"
        fullWidth
        open={addEditAmendment.modal}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>
          <Typography className="dialog-header-title">
            {data?.id ? `${addEditAmendment.mode === "view" ? "View" : "Edit"} Amendment` : "Add Amendment"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ md: 4, xs: 12 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Amendment Date"
                  value={data?.date ? moment(data.date) : null}
                  onChange={(data) => {
                    const isValid = moment(data)?.isValid();
                    onFieldChange("date", data ? moment(data).format(backendDateFormat) : null)
                    if (isValid) {
                      onRestErrorKey("date");
                    }
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      InputLabelProps: { shrink: true },
                      size: "small",
                      margin: "normal",
                      error: error.date || false,
                      helperText: error.date || '',
                    },
                    field: {
                      clearable: true,
                      onClear: () => {
                        onFieldChange("date", null);
                        onRestErrorKey("date");
                      }
                    },
                  }}
                  sx={{ width: "100%" }}
                  format={fieldDateFormat}
                  disabled={modeType === 'view'}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                disabled={addEditAmendment.mode === "view"}
                required
                id="unique_identifier"
                label="Amendment Number"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, input: { maxLength: 25 } }}
                value={data?.unique_identifier || ""}
                onChange={(e) => { const val = e.target.value;
                  return regexConstants.restrictUnicodeCharacters.test(val) || val === "" ? val?.length <= 25 ? onFieldChange("unique_identifier", val.trimStart()) : e.preventDefault() : null
                }}
                error={!!error.unique_identifier}
                helperText={error.unique_identifier || (data?.unique_identifier?.length > 25 ? "Maximum 25 characters allowed" : "")}
                fullWidth
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                  disabled={addEditAmendment.mode === "view"}
                  id="tsn"
                  label="TSN"
                  variant="outlined"
                  size="small"
                  value={addEditAmendment.mode === "view" && data?.tsn !== '' && data?.tsn !== null && data?.tsn !== undefined ? numberWithCommas(data?.tsn) : (data?.tsn || "")}
                  onChange={(e) => regexConstants.numberWithDot.test(e.target.value) || e.target.value === "" ? onFieldChange("tsn", e.target.value.trimStart()) : e.preventDefault() }
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: {maxLength: 10} }}
                />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                  disabled={addEditAmendment.mode === "view"}
                  id="csn"
                  label="CSN"
                  variant="outlined"
                  size="small"
                  value={data?.csn || ""}
                  onChange={(e) => { regexConstants.onlyNumeric.test(e.target.value.trimStart()) || e.target.value === "" ? onFieldChange("csn", e.target.value) : e.preventDefault() }}
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: {maxLength: 10} }}
                />
            </Grid>
            {modeType === 'view' ?
              <Grid item size={{ md: 12, xs: 12 }}>
                <LessMoreTextFieldComp label='Amendment Description' required={true} value={data?.description || ""} />
              </Grid>
              : <Grid size={{ md: 12, xs: 12 }}>
                <TextField
                  disabled={addEditAmendment.mode === "view"}
                  required
                  id="description"
                  label="Amendment Description"
                  variant="outlined"
                  size="small"
                  margin="normal"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                  value={data?.description || ""}
                  onChange={(e) => { onFieldChange("description", e.target.value) }}
                  fullWidth
                  multiline
                  rows={3}
                  error={!!error.description}
                  helperText={error.description}
                />
              </Grid>}
            <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 3 }}>
              <Box sx={{display:"flex",justifyContent:"space-between"}} >
              {data?.id && data?.attachments?.length ?
                <Button onClick={() => { onOpenAttachments(); handleItemClicked('ViewAttachments Button') }} size='small' variant='outlined' color='primary' sx={{ marginBottom: '10px' }}>({addEditAmendment?.data?.attachments?.length})Attachment(s)</Button> : null
              }
               <Button className="available-space-btn" disableRipple size='small' variant='outlined' color='primary' >{`Available Space : ${formatBytes(storageDetails?.remaining_storage)}`}</Button>
              </Box>
              {modeType === 'view' ? null : <AttachementsUpload onFileChange={handleFileChange} attachments={data?.attachments} remainingStorage={storageDetails?.remaining_storage} />}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className="dialog-action">
          <Button className="cancel-btn" disabled={isLoading} variant="outlined" size="small" onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>{modeType === "view" ? "Close" : "Cancel"}</Button>
          {modeType === "view" ? null : 
          <Button variant="contained" size="small" className="btn-primary" onClick={() => { handleItemClicked('Save Button');onAddEditAmendment(); }}> {isLoading ? <CircularProgress color='inherit' size={25} /> : "SAVE"}</Button>}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AmendmentCRU;
